import React from 'react';

import { CloudUploadOutlined } from '@mui/icons-material';
import { Grow } from '@mui/material';

import { isValidPngBase64 } from '../../utils/helpers';
import Alert from '../Alert';
import './_base.scss';

export interface IProps {
  label?: string;
  maxSize: number;
  multiple?: boolean;
  startUploadFiles: (files: File[]) => void;
  setBase64?: (base64File: string | null) => void;
  docType: string;
  multipleDocType?: string[];
  text?: string;
}

export default function DocumentUploader(props: IProps) {
  const {
    docType,
    maxSize,
    startUploadFiles,
    label,
    multiple,
    multipleDocType,
    setBase64,
    text,
  } = props;
  const [error, setError] = React.useState<string | null>(null);
  const [uploaderClassName, setUploaderClassName] = React.useState<string>('');
  const [uploadedByDragging, setUploadedByDragging] =
    React.useState<boolean>(false);
  const handleDragEvents = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    switch (e.type) {
      case 'dragenter':
        setUploaderClassName('dragenter');
        setUploadedByDragging(true);
        break;
      case 'dragleave':
        setUploaderClassName('');
        break;
      case 'dragover':
        setUploaderClassName('dragover');
        break;
      case 'drop':
        setUploaderClassName('');
        handleFileDropped(e);
        break;
      default:
        break;
    }
  };

  const handleFileDropped = (e: any) => {
    setError(null);
    let files = [];
    if (e.target.files && e.target.files.length > 0) {
      files = e.target.files;
    }
    if (e.dataTransfer && e.dataTransfer.files.length > 0) {
      files = e.dataTransfer.files;
    }
    if (docType === '.pdf') {
      for (let i = 0; i < files.length; i++) {
        if (files[i].type !== 'application/pdf') {
          setError(
            'One of the files is not a PDF. Only .pdf file types are permitted for upload'
          );
          return;
        }
      }
    }
    if (docType === '.png') {
      for (let i = 0; i < files.length; i++) {
        if (files[i].type !== 'image/png') {
          setError(
            'One of the files is not a PNG. Only .png file types are permitted for upload.'
          );
          return;
        }
      }
    }

    if (maxSize) {
      for (let i = 0; i < files.length; i++) {
        if (files[i].size > maxSize * 1000000) {
          setError('One of the files is over the ' + maxSize + ' MB limit');
          return;
        }
      }
    }

    for (let i = 0; i < files.length; i++) {
      if (files[i].name.includes(',')) {
        setError(
          'One of the file names contains a comma (,). Please remove it and try again.'
        );
        return;
      }
    }
    if (multipleDocType) {
      let acceptedFileTypes = multipleDocType.join(', ').toUpperCase();
      for (let i = 0; i < files.length; i++) {
        if (!multipleDocType.includes(files[i].type) && files[i].type !== '') {
          if (label === 'Certificate') {
            acceptedFileTypes = '.pem,' + ' .pfx,' + ' .p12';
          }
          if (label === 'Seal') {
            acceptedFileTypes = '.png';
          }
          const errorMessage =
            'Your ' +
            label +
            ' must be a ' +
            acceptedFileTypes +
            ' ' +
            'file type';
          setError(errorMessage);
          return;
        }
      }
    }

    if (multiple === false) {
      const reader = new FileReader();
      const file = uploadedByDragging
        ? e.dataTransfer.files[0]
        : e.target.files[0];
      reader.readAsDataURL(file);
      reader.onload = function () {
        if (setBase64) {
          const base64: any = reader.result;
          if (docType === '.png' && !isValidPngBase64(base64)) {
            setError('The file is not a valid PNG image.');
            return;
          }
          setBase64(base64);
        }
      };
    }

    if (startUploadFiles && files.length > 0) {
      startUploadFiles(files);
    }

    e.target.value = '';
  };
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
  };
  return (
    <Grow in>
      <div>
        <label
          className={'upload-files ' + uploaderClassName}
          onDrop={handleDragEvents}
          onDragEnter={handleDragEvents}
          onDragLeave={handleDragEvents}
          onDragOver={handleDragEvents}
          onMouseDown={handleMouseDown}>
          <input
            type='file'
            accept={docType ? docType : ''}
            hidden
            multiple={multiple}
            onChange={handleFileDropped}
          />
          <CloudUploadOutlined />
          <div>{text ? text : 'Drag and drop files here'}</div>
        </label>
        {error && <Alert text={error} type='warning' />}
      </div>
    </Grow>
  );
}
